import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, ChevronLeft, ChevronRight, ArrowUpRight, RefreshCw } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { usporadatelApi } from "../../services/usporadatelApi";
import { notificationPage } from "../../utils/notificationTopics";
import "./NotificationBell.css";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [topics, setTopics] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const generationRef = useRef(0);
  const acknowledgementsRef = useRef(new Map());
  const location = useLocation();
  const current = notificationPage(topics, page);

  const refresh = useCallback(async () => {
    const generation = ++generationRef.current;
    setLoading(true);
    try {
      const response = await usporadatelApi.listTopics();
      if (generation !== generationRef.current) return;
      setTopics(response.topics.map((topic) => {
        const acknowledged = acknowledgementsRef.current.get(topic.topicId);
        return acknowledged && acknowledged.version >= topic.version ? acknowledged : topic;
      }));
      setError(false);
    } catch {
      if (generation === generationRef.current) setError(true);
    } finally {
      if (generation === generationRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onRead = (event) => {
      const topic = event.detail;
      if (!topic?.topicId) return;
      acknowledgementsRef.current.set(topic.topicId, topic);
      setTopics((items) => items.map((item) => item.topicId === topic.topicId ? topic : item));
      refresh();
    };
    const onFocus = () => { if (!document.hidden) refresh(); };
    const interval = window.setInterval(onFocus, 60000);
    window.addEventListener("focus", onFocus);
    window.addEventListener("anna:topic-read", onRead);
    return () => {
      generationRef.current += 1;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("anna:topic-read", onRead);
    };
  }, [refresh]);

  useEffect(() => { setOpen(false); setPage(0); }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    const onPointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="notification-bell" ref={wrapperRef}>
      <button ref={buttonRef} type="button" className="notification-bell-button"
        title="Notifications" aria-label={`Notifications${current.count ? `, ${current.count} unread` : ""}`}
        aria-expanded={open} aria-controls="notification-popover" aria-haspopup="dialog"
        onClick={() => { setOpen(!open); setPage(0); if (!open) refresh(); }}>
        <Bell size={19} aria-hidden="true" />
        {current.count > 0 && <span className="notification-count">{current.count > 99 ? "99+" : current.count}</span>}
      </button>
      {open && <div id="notification-popover" className="notification-popover" ref={panelRef}
        role="dialog" aria-label="Notifications" tabIndex={-1}>
        <div className="notification-popover-heading">
          <strong>Notifications</strong>
          <button type="button" className="notification-refresh" title="Refresh notifications"
            aria-label="Refresh notifications" onClick={refresh} disabled={loading}>
            <RefreshCw size={15} aria-hidden="true" />
          </button>
        </div>
        <div className="notification-items" aria-busy={loading}>
          {error && <p className="notification-status" role="status">Notifications could not be refreshed.</p>}
          {loading && topics.length === 0 ? <p className="notification-status" role="status">Loading...</p>
            : current.count === 0 && !error ? <p className="notification-status">No new notifications</p>
              : current.items.map((item) => <Link className="notification-item" key={item.topicId}
                to={`/notifications?topic=${encodeURIComponent(item.topicId)}`} onClick={() => setOpen(false)}>
                <span className={`notification-dot${item.topic?.criticality === "critical" ? " critical" : ""}`} />
                <span><strong>{item.topic?.title || "Untitled topic"}</strong>
                  <span className="notification-summary">{item.topic?.status || item.topic?.summary || ""}</span>
                </span>
              </Link>)}
        </div>
        {(current.page > 0 || current.hasMore) && <div className="notification-pagination">
          {current.page > 0 && <button type="button" onClick={() => setPage(current.page - 1)}>
            <ChevronLeft size={14} aria-hidden="true" /> Previous
          </button>}
          {current.hasMore && <button type="button" onClick={() => setPage(current.page + 1)}>
            See more <ChevronRight size={14} aria-hidden="true" />
          </button>}
        </div>}
        <Link className="notification-footer" to="/notifications" onClick={() => setOpen(false)}>
          Open notifications <ArrowUpRight size={15} aria-hidden="true" />
        </Link>
      </div>}
    </div>
  );
}